import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { EyeIcon, EyeOffIcon, PhoneIcon, ShieldCheckIcon, MessageSquareIcon, BotIcon, CheckCircle2Icon } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { rsAnalytics } from "@/lib/rudderstack";
import { resolvePostLoginDestination } from "@/lib/postLoginDestination";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  // Read the ?return= param so we can send users back after login
  const returnPath = new URLSearchParams(window.location.search).get('return');

  useEffect(() => {
    rsAnalytics.track('login_page_viewed');
  }, []);

  // The Google callback refuses an account whose email Google never verified
  // (server/lib/googleEmailVerified) and lands here — say why, or the button
  // just looks broken.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('error') === 'google_email_unverified') {
      toast({
        title: "Google couldn't confirm your email",
        description: "Google hasn't verified the email on that Google account. Verify it with Google first, or sign in with your email instead.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const response = await apiRequest('POST', '/api/auth/login', data);
      return response;
    },
    onSuccess: async (data) => {
      // Two-factor accounts get a challenge instead of a session. Only the
      // LimeCall dashboard has the second step today, and both products share
      // this login endpoint — so without this branch, `data.token` is undefined
      // and we would store the string "undefined" and drop the user into a
      // half-signed-in app that fails on its first request.
      if (data.twoFactorRequired) {
        toast({
          title: "Two-factor authentication is on",
          description: "Finish signing in from the LimeCall dashboard, where you set up your authenticator app.",
          variant: "destructive",
        });
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      
      // Track login event
      if (data.user && data.organization) {
        analytics.trackLogin(
          data.user.id,
          data.organization.id,
          'email'
        );
        rsAnalytics.track('login_completed', {
          userId: data.user.id,
          organizationId: data.organization.id,
          method: 'email'
        });
      }
      
      // If the server says the user must verify their email, route to /verify-email.
      // The OTP has already been sent server-side; the verify-email page lets the
      // user enter it and request a resend.
      if (data.requireEmailVerification) {
        toast({
          title: "Verify your email",
          description: "We sent a verification code to your inbox.",
        });
        window.location.href = '/verify-email';
        return;
      }

      // Team-invite: an existing user arrived via an invite link — attach their
      // account to the inviting org, then land in the app. Non-fatal.
      const inviteToken = new URLSearchParams(window.location.search).get('token');
      if (inviteToken) {
        try { await apiRequest('POST', '/api/auth/accept-invite', { token: inviteToken }); } catch { /* logged in regardless */ }
        toast({ title: "Welcome!", description: "You've joined the team." });
        window.location.href = '/';
        return;
      }

      toast({
        title: "Welcome back!",
        description: "You have been logged in successfully.",
      });

      // Explicit mid-flow returns still win, but default login landings should
      // open the live inbox once the organization already has a number.
      window.location.href = await resolvePostLoginDestination({
        requestedPath: returnPath,
        token: data.token,
      });
    },
    onError: (error) => {
      toast({
        title: "Login Failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: LoginForm) => {
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F8F6] via-[#F7F8F6] to-white flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-ink to-brand-forest p-12 flex-col justify-between relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        
        {/* Logo */}
        <div className="relative z-10">
          <Link href="/" data-testid="link-logo-desktop">
            <div className="flex items-center space-x-3 cursor-pointer group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform shadow-lg">
                <PhoneIcon className="w-7 h-7 text-brand-ink" />
              </div>
              <span className="text-3xl font-bold text-white">
                LimePhone
              </span>
            </div>
          </Link>
        </div>

        {/* Value proposition */}
        <div className="relative z-10 space-y-8">
          <h2 className="text-4xl font-bold text-white leading-tight">
            Professional business communications, powered by AI
          </h2>
          <p className="text-lg text-white/80">
            Join thousands of businesses managing calls, SMS, and leads with intelligent automation.
          </p>
          
          {/* Feature highlights */}
          <div className="space-y-4">
            <div className="flex items-start gap-3 text-white">
              <CheckCircle2Icon className="w-5 h-5 mt-1 flex-shrink-0 text-brand-lime" />
              <span className="text-sm">AI voice agents answer calls 24/7</span>
            </div>
            <div className="flex items-start gap-3 text-white">
              <CheckCircle2Icon className="w-5 h-5 mt-1 flex-shrink-0 text-brand-lime" />
              <span className="text-sm">Intelligent SMS automation and follow-ups</span>
            </div>
            <div className="flex items-start gap-3 text-white">
              <CheckCircle2Icon className="w-5 h-5 mt-1 flex-shrink-0 text-brand-lime" />
              <span className="text-sm">Enterprise-grade security and compliance</span>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="relative z-10 grid grid-cols-3 gap-6 text-white">
          <div data-testid="stat-active-users">
            <div className="text-3xl font-bold text-brand-lime">6K+</div>
            <div className="text-white/70 text-sm">Active Users</div>
          </div>
          <div data-testid="stat-messages">
            <div className="text-3xl font-bold text-brand-lime">10M+</div>
            <div className="text-white/70 text-sm">Messages</div>
          </div>
          <div data-testid="stat-uptime">
            <div className="text-3xl font-bold text-brand-lime">99.9%</div>
            <div className="text-white/70 text-sm">Uptime</div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Link href="/" data-testid="link-logo-mobile">
              <div className="flex items-center space-x-3 cursor-pointer">
                <div className="w-10 h-10 bg-brand-ink rounded-xl flex items-center justify-center shadow-md">
                  <PhoneIcon className="w-5 h-5 text-brand-lime" />
                </div>
                <span className="text-2xl font-bold text-brand-ink">
                  LimePhone
                </span>
              </div>
            </Link>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-slate-900">
                Welcome back
              </h1>
              <p className="text-slate-600">
                Sign in to access your LimePhone dashboard
              </p>
            </div>

            <Card className="border-0 shadow-xl">
              <CardContent className="pt-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      {...form.register("email")}
                      className={`h-11 ${form.formState.errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      data-testid="input-email"
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                        Password
                      </Label>
                      <Link href="/auth/forgot-password">
                        <Button 
                          variant="link" 
                          type="button" 
                          className="text-sm text-brand-call hover:text-brand-call-hover p-0 h-auto font-medium"
                          data-testid="link-forgot-password"
                        >
                          Forgot password?
                        </Button>
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        {...form.register("password")}
                        className={`h-11 pr-10 ${form.formState.errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        data-testid="input-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="button-toggle-password"
                      >
                        {showPassword ? (
                          <EyeOffIcon className="h-4 w-4 text-slate-500" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-slate-500" />
                        )}
                      </Button>
                    </div>
                    {form.formState.errors.password && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-brand-lime hover:bg-brand-lime-hover text-brand-ink font-semibold shadow-lg shadow-brand-lime/30"
                    disabled={loginMutation.isPending}
                    data-testid="button-signin"
                  >
                    {loginMutation.isPending ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-ink mr-2"></div>
                        Signing in...
                      </div>
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </form>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">Or continue with</span>
                    </div>
                  </div>

                  {/* Google Sign In */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-slate-300 hover:bg-slate-50"
                    onClick={() => {
                      window.location.href = '/api/auth/google?returnTo=/mobile';
                    }}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-slate-600">
              Don't have an account?{" "}
              <Link href="/auth/register" className="font-semibold text-brand-call hover:text-brand-call-hover" data-testid="link-signup">
                Start free trial
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
